import React, { useState, useEffect, useCallback } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { useSearch } from '../context/SearchContext';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '../auth/googleDomainAuth.jsx';
import { logger } from '../utils/logger';
import DashboardView from './dashboard/DashboardView';
import {
  ALL_PROGRAMS_OPTION,
  TAB_ENROLLMENT,
  TAB_SELECTIONS,
  TAB_CANCELS,
} from './dashboard/constants';
import { useEnrollmentModel } from './dashboard/useEnrollmentModel';
import { useSnapshotModel } from './dashboard/useSnapshotModel';
import { useSelectionsModel } from './dashboard/useSelectionsModel';
import { useDetailDrawer } from './dashboard/useDetailDrawer';

const Dashboard = ({ onNavigateToDealer }) => {
  const [urlParams, setUrlParams] = useSearchParams();
  const navigate = useNavigate();
  const navigateToDealer = onNavigateToDealer || ((bac, options = {}) => {
    if (!bac) return;
    navigate(`/dealer/${encodeURIComponent(bac)}`, { state: options });
  });
  const { user } = useAuth();
  const theme = useTheme();
  const isCompactDashboard = useMediaQuery(theme.breakpoints.down('md'));
  const isDetailFullScreen = isCompactDashboard;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const appBarOffset = isSmallScreen ? 56 : 64;
  useEffect(() => {
    logger.info('Dashboard bundle active', {
      build: import.meta.env?.VITE_APP_BUILD ?? 'dev',
      timestamp: new Date().toISOString(),
      user: user?.email,
    });
  }, [user?.email]);
  const {
    searchType,
    setSearchType,
    searchValue,
    setSearchValue,
    activeFilter,
    setActiveFilter,
    currentTab,
    setCurrentTab,
    navTabChangeSignal,
    requestTabChange,
    isTableMaximized: dashboardIsTableMaximized,
    setIsTableMaximized,
    setIsDetailOpen,
    setPaginationModel,
  } = useDashboard();

  const [maximizedView, setMaximizedView] = useState(dashboardIsTableMaximized ? 'results' : null);
  const [expandedSections, setExpandedSections] = useState({
    enrollmentStatus: !isCompactDashboard,
    selectionsSummary: !isCompactDashboard,
  });

  useEffect(() => {
    setIsTableMaximized(maximizedView !== null);
  }, [maximizedView, setIsTableMaximized]);

  const {
    useEnrollmentData,
    useAllEnrollmentData,
    fetchProgramSnapshot,
    fetchAllSnapshot,
    fetchDealerSnapshot,
    useDiOrderItemsByReferenceId,
  } = useApi();
  const { mutateAsync: fetchDiOrderItemsByReferenceId } = useDiOrderItemsByReferenceId();

  useEffect(() => {
    logger.info('Dashboard mounted', {
      user: user?.email,
      currentTab,
      searchType,
    });
    return () => {
      logger.info('Dashboard unmounted');
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const queryClient = useQueryClient();
  const [tableSnackbar, setTableSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [gestureHintOpen, setGestureHintOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('gestureHintSeen') !== 'true';
  });

  const { hasSearched, clearSearch } = useSearch();

  const { data: allProgramsData } = useAllEnrollmentData();
  useEffect(() => {
    if (allProgramsData) {
      logger.info('All programs data loaded', { programCount: allProgramsData.length });
    }
  }, [allProgramsData]);

  const { data: programData, isFetching: isProgramFetching } = useEnrollmentData(
    searchType === 'program' && searchValue !== ALL_PROGRAMS_OPTION ? searchValue : null
  );
  useEffect(() => {
    if (!programData) return;
    logger.info('Program data loaded', {
      program: searchValue,
      rows: Array.isArray(programData) ? programData.length : 'unknown',
    });
  }, [programData, searchValue]);

  const {
    displayedData,
    setDisplayedData,
    filteredData,
    runLiveSearch,
    handleRowUpdated,
    syncDisplayedDataForProgram,
    isFetching: isEnrollmentFetching,
    setIsLoading,
  } = useEnrollmentModel({
    queryClient,
    hasSearched,
    searchType,
    searchValue,
    activeFilter,
    allProgramsData,
    programData,
    isProgramFetching,
    setPaginationModel,
  });

  const {
    isSnapshotLoading,
    snapshotDate,
    setSnapshotDate,
    snapshotContext,
    setSnapshotContext,
    snapshotErrors,
    snapshotFailures,
    handleSnapshotLoad,
    handleSnapshotClear,
  } = useSnapshotModel({
    searchType,
    searchValue,
    fetchAllSnapshot,
    fetchDealerSnapshot,
    fetchProgramSnapshot,
    setPaginationModel,
    setDisplayedData,
    runLiveSearch,
    setTableSnackbar,
  });

  useEffect(() => {
    logger.debug('Displayed data updated', { rowCount: displayedData.length });
  }, [displayedData]);

  useEffect(() => {
    logger.debug('Dashboard state change', {
      currentTab,
      searchType,
      searchValue,
      maximizedView,
      activeFilter,
      snapshotDate,
    });
  }, [currentTab, searchType, searchValue, maximizedView, activeFilter, snapshotDate]);

  const toggleResultsMaximize = useCallback(() => {
    setMaximizedView((prev) => (prev === 'results' ? null : 'results'));
  }, []);

  const toggleSelectionsMaximize = useCallback(() => {
    setMaximizedView((prev) => (prev === 'selections' ? null : 'selections'));
  }, []);

  const toggleStatusChangesMaximize = useCallback(() => {
    setMaximizedView((prev) => (prev === 'statusChanges' ? null : 'statusChanges'));
  }, []);

  useEffect(() => {
    setExpandedSections({
      enrollmentStatus: isCompactDashboard ? false : true,
      selectionsSummary: isCompactDashboard ? false : true,
    });
  }, [isCompactDashboard]);

  const handleSectionToggle = useCallback(
    (key) => (_event, expanded) => {
      setExpandedSections((prev) => ({ ...prev, [key]: expanded }));
    },
    []
  );

  useEffect(() => {
    if (snapshotContext) return;
    runLiveSearch();
  }, [runLiveSearch, snapshotContext]);

  useEffect(() => {
    if (!snapshotContext) return;
    setIsLoading(false);
  }, [snapshotContext, setIsLoading]);

  const { detailDrawer, openDetail: handleOpenDetail, isDetailOpen: isDetailDrawerOpen } = useDetailDrawer({
    filteredData,
    navigateToDealer,
    onRowUpdated: handleRowUpdated,
    isDetailFullScreen,
    appBarOffset,
  });
  useEffect(() => {
    setIsDetailOpen(Boolean(isDetailDrawerOpen));
  }, [isDetailDrawerOpen, setIsDetailOpen]);
  useEffect(() => () => setIsDetailOpen(false), [setIsDetailOpen]);

  const {
    referenceFilteredSelectionRows,
    filteredSelectionRows,
    statusMatchChartData,
    setStatusMatchFilter,
  } = useSelectionsModel({
    filteredData,
    referenceIdParam: urlParams.get('referenceId'),
    fetchDiOrderItemsByReferenceId,
    setTableSnackbar,
  });

  useEffect(() => {
    if (!navTabChangeSignal) return;
    setActiveFilter(null);
    clearSearch();
    if (currentTab !== TAB_SELECTIONS) {
      setStatusMatchFilter(null);
    }
    if (currentTab !== TAB_ENROLLMENT && currentTab !== TAB_SELECTIONS) {
      setSnapshotContext(null);
      setSnapshotDate('');
    }
  }, [
    navTabChangeSignal,
    currentTab,
    clearSearch,
    setActiveFilter,
    setSnapshotContext,
    setSnapshotDate,
    setStatusMatchFilter,
  ]);

  const isSelectionFilter = activeFilter?.type === 'selection';
  const isWithoutSelectionsFilter = isSelectionFilter && activeFilter?.value === 'Without Selections';

  const handleStatusChartClick = ({ programName, status }) => {
    clearSearch();
    setSnapshotContext(null);
    setSnapshotDate('');
    setActiveFilter({ type: 'status', programName, value: status });
    setSearchType('program');
    setSearchValue(programName);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    syncDisplayedDataForProgram(programName);
  };

  const handleSelectionChartClick = ({ programName, selectionStatus }) => {
    clearSearch();
    setSnapshotContext(null);
    setSnapshotDate('');
    setActiveFilter({ type: 'selection', programName, value: selectionStatus });
    setSearchType('program');
    setSearchValue(programName);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    syncDisplayedDataForProgram(programName);
    setCurrentTab(TAB_SELECTIONS);
  };

  const updateUrlParams = useCallback(
    (updates) => {
      const params = new URLSearchParams(urlParams);
      Object.entries(updates || {}).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      setUrlParams(params, { replace: true });
    },
    [setUrlParams, urlParams]
  );

  const clearActiveFilter = () => {
    setActiveFilter(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCancelsChartClick = useCallback(
    ({ programName }) => {
      if (programName) {
        updateUrlParams({ cancelProgram: programName });
      } else {
        updateUrlParams({ cancelProgram: null });
      }
      requestTabChange(TAB_CANCELS);
    },
    [requestTabChange, updateUrlParams]
  );

  const handleStatusMatchFilterChange = useCallback(
    (label) => {
      setStatusMatchFilter((prev) => (prev === label ? null : label));
    },
    [setStatusMatchFilter]
  );

  const isFetching = isEnrollmentFetching || isSnapshotLoading;

  const handlePullToRefresh = useCallback(async () => {
    // Lightweight UI refresh: refetch existing queries, without forcing a backend cache refresh.
    await queryClient.invalidateQueries({
      predicate: (q) => {
        const key = q.queryKey?.[0];
        return (
          key === 'enrollmentData' ||
          key === 'allEnrollmentData' ||
          key === 'cacheStatus' ||
          key === 'applicationMetrics' ||
          key === 'statusChangeMetrics' ||
          key === 'statusChanges' ||
          key === 'snapshots' ||
          key === 'actionRequiredWatch' ||
          key === 'actionRequiredWatchHistory' ||
          key === 'cancelledBacsReport'
        );
      },
    });
  }, [queryClient]);

  const handleDismissGestureHint = useCallback(() => {
    setGestureHintOpen(false);
    try {
      window.localStorage.setItem('gestureHintSeen', 'true');
    } catch (_err) {
      // noop
    }
  }, []);

  const handleCloseTableSnackbar = useCallback(() => {
    setTableSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <DashboardView
      user={user}
      hasSearched={hasSearched}
      navigateToDealer={navigateToDealer}
      handleOpenDetail={handleOpenDetail}
      detailDrawer={detailDrawer}
      maximizedView={maximizedView}
      toggleResultsMaximize={toggleResultsMaximize}
      toggleSelectionsMaximize={toggleSelectionsMaximize}
      toggleStatusChangesMaximize={toggleStatusChangesMaximize}
      filteredData={filteredData}
      filteredSelectionRows={filteredSelectionRows}
      referenceFilteredSelectionRows={referenceFilteredSelectionRows}
      statusMatchChartData={statusMatchChartData}
      handleStatusMatchFilterChange={handleStatusMatchFilterChange}
      currentTab={currentTab}
      setCurrentTab={setCurrentTab}
      isCompactDashboard={isCompactDashboard}
      isSmallScreen={isSmallScreen}
      expandedSections={expandedSections}
      handleSectionToggle={handleSectionToggle}
      gestureHintOpen={gestureHintOpen}
      handleDismissGestureHint={handleDismissGestureHint}
      handlePullToRefresh={handlePullToRefresh}
      isFetching={isFetching}
      activeFilter={activeFilter}
      clearActiveFilter={clearActiveFilter}
      handleStatusChartClick={handleStatusChartClick}
      handleSelectionChartClick={handleSelectionChartClick}
      handleCancelsChartClick={handleCancelsChartClick}
      searchType={searchType}
      setSearchType={setSearchType}
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      snapshotDate={snapshotDate}
      setSnapshotDate={setSnapshotDate}
      isSnapshotLoading={isSnapshotLoading}
      snapshotContext={snapshotContext}
      snapshotErrors={snapshotErrors}
      snapshotFailures={snapshotFailures}
      handleSnapshotLoad={handleSnapshotLoad}
      handleSnapshotClear={handleSnapshotClear}
      isWithoutSelectionsFilter={isWithoutSelectionsFilter}
      handleRowUpdated={handleRowUpdated}
      tableSnackbar={tableSnackbar}
      handleCloseTableSnackbar={handleCloseTableSnackbar}
    />
  );
};

export default Dashboard;
